import { EDimOrientation, IDimPrimitive } from '../plan-primitive';
import {
    EDxfEntityKind,
    EDxfHorizontalAlign,
    EDxfVerticalAlign,
    IDxfBounds,
    IDxfLine,
    IDxfPoint,
    IDxfText,
    boundsOfPoints,
    textCorners,
    textWidth,
} from './dxf-entities';
import { IDxfPaneFit, toPaneMm } from './dxf-fit';
import { EDxfLayerKind, paneLayerName } from './dxf-layers';
import { IDxfSegment, IDxfSketch, boxesMeet, segmentHitsBox } from './dxf-sketch';
import { toDxfText } from './dxf-text';

/*
 * A dimension by ГОСТ 2.307 with the slashes of ГОСТ 21.101 (СПДС): thin extension lines from
 * the measured points running 2 mm past the dimension line, 45° slashes 3 mm long where the two
 * meet, the dimension line carried 2 mm past the extension lines, and the number 1 mm above the
 * line, parallel to it. All sizes are paper millimetres, multiplied by the view's denominator.
 */

/** Height of a dimension number on paper, millimetres (ГОСТ 2.304 size 2.5). */
export const DXF_DIM_TEXT_MM: number = 2.5;
/** Length of a dimension slash on paper, millimetres. */
export const DXF_DIM_SLASH_MM: number = 3;
/** How far the dimension line runs past an extension line on paper, millimetres. */
export const DXF_DIM_OVERRUN_MM: number = 2;
/** How far an extension line runs past the dimension line on paper, millimetres. */
export const DXF_DIM_EXTENSION_MM: number = 2;
/** Gap between the dimension line and its number on paper, millimetres. */
export const DXF_DIM_TEXT_GAP_MM: number = 1;
/** A measured point no farther than this from the dimension line, on paper, needs no extension line. */
const EXTENSION_MIN_MM: number = 0.5;
/** How close a vertex must lie to a dimension end along the measured axis to be its point, model millimetres. */
const FEATURE_TOLERANCE_MM: number = 0.05;

/** The axis a dimension measures along (0 = X) and the one across it. */
export interface IDimAxes {
    along: 0 | 1;
    across: 0 | 1;
}

export function axesOf(prim: IDimPrimitive): IDimAxes {
    return prim.orientation === EDimOrientation.Horizontal ? { along: 0, across: 1 } : { along: 1, across: 0 };
}

export function line(layer: string, scale: number, from: IDxfPoint, to: IDxfPoint): IDxfLine {
    return { kind: EDxfEntityKind.Line, ltScale: scale, layer, from, to };
}

export function pointOn(axes: IDimAxes, along: number, across: number): IDxfPoint {
    return axes.along === 0 ? [along, across] : [across, along];
}

/**
 * The point a dimension end measures: the vertex of the view's drawing on the same line across
 * the dimension, nearest to it. Null when the drawing has none there.
 */
export function measuredPoint(end: IDxfPoint, axes: IDimAxes, sketch: IDxfSketch): IDxfPoint | null {
    let best: IDxfPoint | null = null;
    for (const vertex of sketch.vertices) {
        if (Math.abs(vertex[axes.along] - end[axes.along]) > FEATURE_TOLERANCE_MM) {
            continue;
        }
        if (!best || Math.abs(vertex[axes.across] - end[axes.across]) < Math.abs(best[axes.across] - end[axes.across])) {
            best = vertex;
        }
    }
    return best;
}

/** The extension line from the measured point to 2 mm past the dimension line; none when they touch. */
export function extensionLine(end: IDxfPoint, measured: IDxfPoint | null, axes: IDimAxes, layer: string, scale: number): IDxfLine[] {
    if (!measured) {
        return [];
    }
    const gap: number = end[axes.across] - measured[axes.across];
    if (Math.abs(gap) <= EXTENSION_MIN_MM * scale) {
        return [];
    }
    const past: number = end[axes.across] + Math.sign(gap) * DXF_DIM_EXTENSION_MM * scale;
    return [line(layer, scale, measured, pointOn(axes, end[axes.along], past))];
}

/** A 45° slash centred on a dimension end, rising to the right. */
export function slash(end: IDxfPoint, layer: string, scale: number): IDxfLine {
    const half: number = (DXF_DIM_SLASH_MM * scale) / 2 / Math.SQRT2;
    return line(layer, scale, [end[0] - half, end[1] - half], [end[0] + half, end[1] + half]);
}

/** The number of a dimension, above the line: left of a vertical one, read from below, unless that side is taken. */
function dimLabel(prim: IDimPrimitive, fit: IDxfPaneFit, mid: IDxfPoint, axes: IDimAxes, sketch: IDxfSketch): IDxfText {
    const scale: number = fit.scale;
    const height: number = DXF_DIM_TEXT_MM * scale;
    const gap: number = DXF_DIM_TEXT_GAP_MM * scale;
    const base: IDxfText = {
        kind: EDxfEntityKind.Text,
        layer: paneLayerName(fit.paneId, EDxfLayerKind.Dim),
        ltScale: scale,
        at: mid,
        text: toDxfText(prim.label),
        rotationDeg: 0,
        hAlign: EDxfHorizontalAlign.Center,
        vAlign: EDxfVerticalAlign.Bottom,
        widthFactor: 1,
        height,
    };
    if (axes.along === 0) {
        return { ...base, at: [mid[0], mid[1] + gap] };
    }
    const left: IDxfText = { ...base, at: [mid[0] - gap, mid[1]], rotationDeg: 90 };
    const box: IDxfBounds = boundsOfPoints(textCorners(left));
    const taken: boolean =
        sketch.segments.some((segment: IDxfSegment): boolean => segmentHitsBox(segment, box)) ||
        sketch.labels.some((label: IDxfBounds): boolean => boxesMeet(label, box));
    // The sheet keeps the right side clear for the number; the left side is used when it is free.
    return taken ? { ...left, at: [mid[0] + gap + height, mid[1]] } : left;
}

/** The dimension line carried on from the nearer end of its span to the far side of a number moved outside it. */
function leader(from: IDxfPoint, to: IDxfPoint, axes: IDimAxes, label: IDxfText, layer: string, scale: number): IDxfLine[] {
    const at: number = label.at[axes.along];
    const low: number = Math.min(from[axes.along], to[axes.along]);
    const high: number = Math.max(from[axes.along], to[axes.along]);
    if (at >= low && at <= high) {
        return [];
    }
    const half: number = textWidth(label.text, label.height) / 2;
    const start: number = at > high ? high : low;
    const finish: number = at > high ? at + half : at - half;
    const across: number = from[axes.across];
    return [line(layer, scale, pointOn(axes, start, across), pointOn(axes, finish, across))];
}

/** A dimension as entities in the view's own millimetres: line, slashes, extension lines and number. */
export function dimEntities(prim: IDimPrimitive, fit: IDxfPaneFit, sketch: IDxfSketch): (IDxfLine | IDxfText)[] {
    const scale: number = fit.scale;
    const layer: string = paneLayerName(fit.paneId, EDxfLayerKind.Dim);
    const axes: IDimAxes = axesOf(prim);
    const from: IDxfPoint = toPaneMm(fit, prim.from);
    const to: IDxfPoint = toPaneMm(fit, prim.to);
    const [low, high]: [IDxfPoint, IDxfPoint] = from[axes.along] <= to[axes.along] ? [from, to] : [to, from];
    const overrun: number = DXF_DIM_OVERRUN_MM * scale;
    const across: number = from[axes.across];
    // A number moved past an end of its span moves in millimetres too; the file's Y runs up, the screen's down.
    const shift: number = ((prim.labelShift ?? 0) / fit.pxPerMm) * (axes.along === 0 ? 1 : -1);
    const mid: IDxfPoint = pointOn(axes, (low[axes.along] + high[axes.along]) / 2 + shift, across);
    const label: IDxfText = dimLabel(prim, fit, mid, axes, sketch);
    sketch.labels.push(boundsOfPoints(textCorners(label)));
    return [
        line(layer, scale, pointOn(axes, low[axes.along] - overrun, across), pointOn(axes, high[axes.along] + overrun, across)),
        ...extensionLine(low, measuredPoint(low, axes, sketch), axes, layer, scale),
        ...extensionLine(high, measuredPoint(high, axes, sketch), axes, layer, scale),
        slash(low, layer, scale),
        slash(high, layer, scale),
        ...leader(from, to, axes, label, layer, scale),
        label,
    ];
}
